// Keep <input type="range"> progress visually consistent across browsers.
// WebKit/Blink doesn't expose a separate "progress" pseudo-element, so we drive
// the filled portion via a CSS var that our SCSS reads:
//   --vyby-range-p: "0%".."100%"

function clamp01(n) {
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > 1) return 1
  return n
}

function toNumber(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function applyRangeProgress(el) {
  if (!el || el.tagName !== 'INPUT' || el.type !== 'range') return
  const min = toNumber(el.min) ?? 0
  const max = toNumber(el.max) ?? 100
  const val = toNumber(el.value) ?? min
  const denom = max - min
  const t = denom === 0 ? 0 : (val - min) / denom
  const pct = clamp01(t) * 100
  el.style.setProperty('--vyby-range-p', `${pct}%`)
}

let _rangeProgressInited = false
let _rangeProgressObserver = null

export function initRangeProgress() {
  if (typeof document === 'undefined') return
  if (_rangeProgressInited) return
  _rangeProgressInited = true

  const selector = 'input[type="range"].vyby-control, input[type="range"].vyby-range'

  const onAny = (e) => {
    const el = e?.target
    if (!el || typeof el.matches !== 'function') return
    if (!el.matches(selector)) return
    applyRangeProgress(el)
  }

  document.addEventListener('input', onAny, true)
  document.addEventListener('change', onAny, true)

  const scan = (root) => {
    try {
      if (!root) return
      // Fast path: direct element match
      if (root.matches && root.matches(selector)) {
        applyRangeProgress(root)
        return
      }
      // Otherwise, search within subtree
      if (root.querySelectorAll) {
        root.querySelectorAll(selector).forEach((el) => applyRangeProgress(el))
      }
    } catch (e) {
      // noop
    }
  }

  // Initialize existing sliders after mount.
  requestAnimationFrame(() => scan(document))

  // Also initialize sliders that appear later (e.g. panels/overlays that mount/unmount).
  if (typeof MutationObserver !== 'undefined' && document.body) {
    let scheduled = false
    const scheduleFullScan = () => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        scan(document)
      })
    }

    _rangeProgressObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (!m || !m.addedNodes || !m.addedNodes.length) continue
        // If any added node is (or contains) a range input, update quickly.
        for (const n of m.addedNodes) {
          if (!n) continue
          if (n.nodeType !== 1) continue // ELEMENT_NODE
          scan(n)
        }
      }
      // Fallback scan to catch attribute-only changes / missed nodes.
      scheduleFullScan()
    })

    _rangeProgressObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }
}

